import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { useSnapshot } from "@/store/site";
import { useWorstAlarm } from "@/hooks/useWorstAlarm";
import { useIncidentDismissed } from "@/hooks/useIncidentDismissed";
import { useDischargePrice } from "@/hooks/useDischargePrice";
import { MODE_CATALOG, MODE_TONE } from "@/data/modeCatalog";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { formatEur, formatHourMin } from "@/lib/format";

/**
 * N02 + N09 — the persistent GM verdict, carried into the page shell so
 * off-Overview routes still answer "is the site healthy / making money /
 * what's it doing" without navigating. Today that story lives only on
 * /overview's HealthBanner; this is its quiet, glanceable echo elsewhere.
 *
 * IN-FLOW, deliberately NOT fixed/sticky. PageShell renders this inside its
 * normal title-row document flow (to the LEFT of the title), so it scrolls
 * with the page header and collides with NOTHING. The four existing
 * `fixed top-12` surfaces — IncidentStrip, SimulatedDataBanner,
 * TelemetryOutageStrip (top-12 / top-20 stacked) — already own that overlay
 * lane and each add their own in-flow spacer; a fifth fixed bar would bury
 * the page on a ~360px ThingsBoard column. So this needs no spacer math and
 * never stacks with them.
 *
 * Coordination / suppression (mirrors IncidentStrip's pathname guard so the
 * verdict and the critical strip can never both show):
 *  - /overview      → HealthBanner already owns the full verdict there.
 *  - /controller    → ModeReasoningCard already shows richer plant state.
 *  - criticalActive AND the IncidentStrip is showing → the live strip owns the
 *    surface (it renders on exactly the non-overview routes this would); never
 *    two verdicts at once. But if the operator DISMISSES the strip (R09), the
 *    strip stops showing, so we fall through to the critical-tone segment here
 *    — the page keeps an in-flow verdict instead of losing both surfaces.
 *  - isError        → health is UNKNOWN; suppress rather than render a verdict
 *    that can't be trusted (the topbar pill + IncidentStrip carry the outage).
 * The calm HEALTHY state is intentionally KEPT — it is the whole point.
 *
 * Data is REUSED, never recomputed into a new figure that can desync:
 *  - health  → useWorstAlarm() + snap.health.overall (same inputs as HealthBanner).
 *  - money   → useDischargePrice() × snap.energy_today.bess_discharge_kWh, gated
 *    on the identical `moneyKnown` (positive price + finite kWh) so it never
 *    fabricates €0.
 *  - mode    → snap.controller (the same source ModeReasoningCard reads). The
 *    override clause shows the ABSOLUTE HH:MM only — no live ticking
 *    relative-time, so the shell never re-renders per second on every route
 *    (the full countdown stays on ModeReasoningCard).
 *
 * Narrow-iframe safe (the host flex-wraps), print:hidden like the other strips.
 */
export function ShellVerdict() {
  const { pathname } = useLocation();
  const snap = useSnapshot();
  const t = useT();
  const { isError, active, criticalActive, top } = useWorstAlarm();
  const dismissedId = useIncidentDismissed((s) => s.dismissedId);
  const { price: dischargePrice, isReady: priceReady } = useDischargePrice();

  // Suppress where another surface already owns the verdict, or where it
  // can't be trusted. Mirrors IncidentStrip's startsWith pathname guard.
  if (pathname.startsWith("/overview")) return null;
  if (pathname.startsWith("/controller")) return null;
  if (isError) return null;
  // A live critical promotes the IncidentStrip on these same non-overview
  // routes — let it own the surface. But ONLY while the strip is actually
  // showing: once the operator dismisses it (R09 — shared dismissedId), the
  // strip returns null, so we must fall through and render the verdict here
  // (its critical-tone health segment) rather than leaving the page with no
  // verdict at all. Mirrors IncidentStrip's `top.id === dismissedId` guard.
  const stripShowing = criticalActive && !(top && dismissedId === top.id);
  if (stripShowing) return null;

  const overall = snap.health.overall;
  const healthy = active.length === 0 && overall === "ok";

  // Money — REUSE HealthBanner's exact tariff + gate (F08). Never fabricate €0.
  const discharge_kWh = Number.isFinite(snap.energy_today.bess_discharge_kWh)
    ? snap.energy_today.bess_discharge_kWh
    : NaN;
  const moneyKnown = priceReady && dischargePrice > 0 && Number.isFinite(discharge_kWh);
  const moneySaved = moneyKnown ? discharge_kWh * dischargePrice : 0;

  // Health segment — one word + tone, derived from the SAME inputs as
  // HealthBanner so the two can never disagree. (We never reach here when
  // criticalActive — the IncidentStrip owns that — but the degraded/warn
  // path is still reachable for acknowledged/reported degradation.)
  const HealthIcon = healthy ? CheckCircle2 : overall === "critical" ? ShieldAlert : AlertTriangle;
  const healthLabel = healthy
    ? t("shell.verdict.healthy")
    : active.length > 0
      ? t(active.length === 1 ? "shell.verdict.attention.one" : "shell.verdict.attention.other", { n: active.length })
      : t("shell.verdict.degraded");
  const healthTone = healthy ? "text-ok" : overall === "critical" ? "text-critical" : "text-warn";

  // Plant-state chip (N09) — same source as ModeReasoningCard.
  const mode = snap.controller.active_mode;
  const modeLabel = MODE_CATALOG[mode]?.label ?? mode;
  const modeTone = MODE_TONE[mode] ?? MODE_TONE.gridZeroControl;
  const overrideActive = snap.controller.schedule_source === "manual_override";
  const overrideExpires = snap.controller.override_expires_at;

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted print:hidden"
      role="status"
      aria-live="off"
    >
      {/* Health — links to /alarms, carrying context. R13: a quiet persistent
          dotted underline so non-power users read the segment as clickable;
          hover:text-accent stays. */}
      <Link
        to={carryTo("/alarms")}
        className={cn("flex shrink-0 items-center gap-1 underline decoration-dotted decoration-text-faint underline-offset-2 hover:text-accent", healthTone)}
        title={t("shell.verdict.healthTitle")}
      >
        <HealthIcon size={13} className="shrink-0" />
        <span className="font-medium">{healthLabel}</span>
      </Link>

      {/* Money — same tariff/gate as HealthBanner; links to /revenue. Hidden
          only when the figure isn't known (never a fabricated €0). R02: the
          dedicated shell.verdict.moneyToday keeps the "today" qualifier so the
          €-line never reads as ambiguous lifetime savings. R13: dotted
          underline affordance. */}
      {moneyKnown && (
        <>
          <span aria-hidden className="text-text-faint">·</span>
          <Link
            to={carryTo("/revenue")}
            className="shrink-0 underline decoration-dotted decoration-text-faint underline-offset-2 hover:text-accent"
            title={t("monitor.health.moneyClauseTitle")}
          >
            {t("shell.verdict.moneyToday", { eur: formatEur(moneySaved, 0) })}
          </Link>
        </>
      )}

      {/* Plant-state chip — active mode (+ absolute override expiry); links to
          /controller for a fast jump. R13: the bordered badge already reads as
          interactive, so its clickability cue is the destination title (the
          badge summary moves onto the inner span). R14: the override
          sub-clause is md-gated (hidden md:inline) so on the ~360px TB column
          the verdict+IncidentStrip+ViewTabs stack can't push the H1 below the
          fold (the full countdown stays on ModeReasoningCard). */}
      <span aria-hidden className="text-text-faint">·</span>
      <Link
        to={carryTo("/controller")}
        className="flex shrink-0 items-center gap-1.5 hover:opacity-90"
        title={t("shell.verdict.modeTitle")}
      >
        <span
          title={MODE_CATALOG[mode]?.summary}
          className={cn(
            "rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
            modeTone,
          )}
        >
          {modeLabel}
        </span>
        {overrideActive && overrideExpires && (
          <span className="hidden text-[10px] font-medium text-warn md:inline">
            {t("shell.verdict.overrideUntil", { time: formatHourMin(overrideExpires) })}
          </span>
        )}
      </Link>
    </div>
  );
}
